/**
 * Utility functions for handling ObjectId detection and data extraction
 */

/**
 * Check if a string is a MongoDB ObjectId
 * @param {string} str - String to check
 * @returns {boolean} - True if string is an ObjectId
 */
export const isObjectId = (str) => {
  if (!str || typeof str !== "string") return false;
  return str.length === 24 && /^[0-9a-fA-F]{24}$/.test(str);
};

/**
 * Extract meaningful value from a field that might be an ObjectId or populated object
 * @param {any} field - Field value to process
 * @param {string} fallback - Fallback value if field is ObjectId or invalid
 * @param {string} extractProperty - Property to extract from populated object
 * @returns {string} - Processed value
 */
export const extractValue = (
  field,
  fallback = "Not specified",
  extractProperty = "name",
) => {
  if (!field) return fallback;

  // If it's a string, check if it's an ObjectId
  if (typeof field === "string") {
    if (isObjectId(field)) {
      return fallback; // Return fallback for ObjectId
    }
    return field;
  }

  // If it's an object, try to extract the specified property
  if (typeof field === "object" && field !== null) {
    // Try common properties for location objects
    if (extractProperty === "name" && !field.name) {
      return field.address || field.city || field.display_name || fallback;
    }
    return field[extractProperty] || fallback;
  }

  return fallback;
};

/**
 * Format distance value, handling NaN and zero values
 * @param {any} distance - Distance value to format
 * @returns {string} - Formatted distance string
 */
export const formatDistance = (distance) => {
  const dist = parseFloat(distance || 0);
  if (isNaN(dist) || dist === 0) {
    return "Not specified";
  }
  return `${Math.round(dist).toLocaleString("en-IN")} km`;
};

/**
 * Format time duration from distance
 * @param {any} distance - Distance in km
 * @param {number} avgSpeed - Average speed in km/h (default: 60)
 * @returns {string} - Formatted time string
 */
export const formatTravelTime = (distance, avgSpeed = 60) => {
  const dist = parseFloat(distance || 0);
  if (isNaN(dist) || dist === 0) {
    return "Not specified";
  }

  const hours = Math.floor(dist / avgSpeed);
  const minutes = Math.round((dist % avgSpeed) * (60 / avgSpeed));

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }
  return `${minutes} min`;
};

/**
 * Process location data to extract name and address
 * @param {any} location - Location object or string
 * @param {string} fallback - Fallback value
 * @returns {object} - Object with name and address properties
 */
export const processLocation = (
  location,
  fallback = "Location not specified",
) => {
  if (!location) {
    return { name: fallback, address: null };
  }

  // If it's a string, check if it's an ObjectId
  if (typeof location === "string") {
    if (isObjectId(location)) {
      return { name: fallback, address: null };
    }
    return { name: location, address: null };
  }

  // If it's an object, try to extract name and address
  if (typeof location === "object" && location !== null) {
    const name =
      location.name || location.city || location.display_name || fallback;
    const address = location.address || location.formatted_address || null;

    return { name, address };
  }

  return { name: fallback, address: null };
};
